import { Bold, Italic, Link2, Lock, LockOpen, RotateCcw, Underline, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { contrastRatio, fixContrast, grade, lightnessDelta } from "@/color/contrast";
import type { Hex } from "@/color/oklch";
import { makeContext } from "@/exporters/types";
import { weechatColor } from "@/exporters/weechat";
import { PALETTE_INFO, PALETTE_KEYS, type PaletteKey } from "@/model/palette";
import { type ColorId, describeExpr, hexOf, referencesTo } from "@/model/resolve";
import { DEFAULT_STYLES } from "@/model/theme";
import {
  defaultExpr,
  GROUPS,
  type Ref,
  type SyntaxKey,
  TOKEN_MAP,
  TOKENS,
  type TokenId,
} from "@/model/tokens";
import { useEditor } from "@/store/editor";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Tip } from "@/ui/kit/tooltip";
import { GradeBadge } from "../controls/badges";
import { OklchPicker } from "../controls/oklch-picker";
import { Swatch } from "../controls/swatch";

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="space-y-2 border-line border-b px-4 py-3">
      <div className="flex items-center">
        <h3 className="flex-1 font-medium text-[11px] text-faint uppercase tracking-wider">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ContrastCard({
  fg,
  bg,
  min,
  level,
  onFix,
}: {
  fg: Hex;
  bg: Hex;
  min: number;
  level: string;
  onFix?: () => void;
}) {
  const ratio = contrastRatio(fg, bg);
  const g = grade(ratio, min);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line bg-bg p-2.5">
      <div
        className="flex h-10 w-14 items-center justify-center rounded-md font-mono font-semibold text-sm"
        style={{ background: bg, color: fg }}
      >
        Aa
      </div>
      <div className="min-w-0 flex-1 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-fg text-sm tabular-nums">
            {ratio.toFixed(2)}:1
          </span>
          <GradeBadge grade={g} />
        </div>
        <div className="text-faint">
          {level} needs ≥ {min}:1 · ΔL {lightnessDelta(fg, bg).toFixed(0)}
        </div>
      </div>
      {g !== "pass" && onFix && (
        <Tip label="Shift lightness until it passes">
          <Button size="sm" variant="outline" onClick={onFix}>
            <Wand2 /> Fix
          </Button>
        </Tip>
      )}
    </div>
  );
}

function UsedBy({ refText }: { refText: string }) {
  const draft = useEditor((s) => s.draft);
  const select = useEditor((s) => s.select);
  const ids = useMemo(() => referencesTo(draft, refText), [draft, refText]);
  const [all, setAll] = useState(false);
  if (ids.length === 0)
    return <p className="text-faint text-xs">Nothing references this directly.</p>;
  const shown = all ? ids : ids.slice(0, 12);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((id) => {
        const def = TOKEN_MAP.get(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => select({ kind: "token", id })}
            className="rounded border border-line px-1.5 py-0.5 text-[11px] text-muted hover:border-accent hover:text-fg"
          >
            {GROUPS.find((g) => g.id === def?.group)?.label} · {def?.label}
          </button>
        );
      })}
      {!all && ids.length > shown.length && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="px-1.5 py-0.5 text-[11px] text-accent"
        >
          +{ids.length - shown.length} more
        </button>
      )}
    </div>
  );
}

function PaletteInspector({ k }: { k: PaletteKey }) {
  const hex = useEditor((s) => s.draft.palette[k]);
  const bg = useEditor((s) => s.draft.palette.bg1);
  const locked = useEditor((s) => s.draft.locks.includes(k));
  const thresholds = useEditor((s) => s.thresholds);
  const edit = useEditor((s) => s.edit);
  const setHex = (next: Hex) =>
    edit((t) => ({ ...t, palette: { ...t.palette, [k]: next } }), `palette.${k}`);
  const isBg = k.startsWith("bg");
  return (
    <>
      <div className="space-y-3 border-line border-b px-4 py-4">
        <Swatch hex={hex} size="lg" aria-label={hex} />
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{PALETTE_INFO[k].label}</div>
            <div className="text-muted text-xs">
              <span className="font-mono">${k}</span> · {PALETTE_INFO[k].hint}
            </div>
          </div>
          <Tip
            label={
              locked ? "Unlock: generators may change it" : "Lock: generators and adjusters skip it"
            }
          >
            <Button
              size="icon-sm"
              variant={locked ? "default" : "ghost"}
              aria-label={locked ? "Unlock" : "Lock"}
              onClick={() =>
                edit((t) => ({
                  ...t,
                  locks: locked ? t.locks.filter((x) => x !== k) : [...t.locks, k],
                }))
              }
            >
              {locked ? <Lock /> : <LockOpen />}
            </Button>
          </Tip>
        </div>
        <OklchPicker key={k} value={hex} onChange={setHex} />
      </div>
      {!isBg && k !== "bg1" && (
        <Section title="On the base background">
          <ContrastCard
            fg={hex}
            bg={bg}
            min={thresholds.text}
            level="text"
            onFix={() => setHex(fixContrast(hex, bg, thresholds.text))}
          />
        </Section>
      )}
      <Section title="Used by">
        <UsedBy refText={`$${k}`} />
      </Section>
    </>
  );
}

function SourcePicker({ id }: { id: TokenId }) {
  const def = TOKEN_MAP.get(id);
  const palette = useEditor((s) => s.draft.palette);
  const variant = useEditor((s) => s.draft.variant);
  const override = useEditor((s) => s.draft.overrides[id]);
  const edit = useEditor((s) => s.edit);
  const [linking, setLinking] = useState(false);
  const [q, setQ] = useState("");
  if (!def) return null;
  const setRef = (ref: Ref | undefined) =>
    edit((t) => {
      const overrides = { ...t.overrides };
      if (ref === undefined) delete overrides[id];
      else overrides[id] = ref;
      return { ...t, overrides };
    });
  const derived = describeExpr(defaultExpr(def, variant));
  const candidates = TOKENS.filter(
    (t) =>
      t.id !== id && (!q || t.label.toLowerCase().includes(q.toLowerCase()) || t.id.includes(q)),
  ).slice(0, 40);
  return (
    <Section
      title="Source"
      action={
        override && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRef(undefined)}
            aria-label="Reset to derived"
          >
            <RotateCcw /> Reset
          </Button>
        )
      }
    >
      <div className="text-xs">
        {override ? (
          <span>
            Overridden: <span className="font-mono text-accent">{override}</span>{" "}
            <span className="text-faint">(derived: {derived})</span>
          </span>
        ) : (
          <span className="text-muted">
            Derived from <span className="font-mono text-fg">{derived}</span>, follows the palette
          </span>
        )}
      </div>
      <div className="grid grid-cols-9 gap-1">
        {PALETTE_KEYS.map((k) => (
          <Tip key={k} label={`$${k}`} side="top">
            <Swatch
              hex={palette[k]}
              size="sm"
              className="size-6"
              selected={override === `$${k}`}
              onClick={() => setRef(`$${k}`)}
              aria-label={`Link to $${k}`}
            />
          </Tip>
        ))}
      </div>
      <div>
        <Button size="sm" variant="ghost" onClick={() => setLinking((x) => !x)}>
          <Link2 /> Link to another token
        </Button>
        {linking && (
          <div className="mt-1 space-y-1 rounded-lg border border-line bg-bg p-1.5">
            <input
              // biome-ignore lint/a11y/noAutofocus: opened by an explicit click on "Link to another token"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tokens…"
              className="h-7 w-full rounded border border-line bg-panel px-2 text-xs outline-none focus:border-accent"
            />
            <div className="max-h-44 overflow-auto">
              {candidates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setRef(`@${t.id}`);
                    setLinking(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-raised"
                >
                  <span className="text-faint">{GROUPS.find((g) => g.id === t.group)?.label}</span>
                  <span className="truncate">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function StyleToggles({ k }: { k: SyntaxKey }) {
  const style = useEditor((s) => s.resolved.styles[k]);
  const custom = useEditor((s) => k in s.draft.styles);
  const edit = useEditor((s) => s.edit);
  const toggle = (flag: "bold" | "italic" | "underline") =>
    edit((t) => ({ ...t, styles: { ...t.styles, [k]: { ...style, [flag]: !style[flag] } } }));
  return (
    <Section
      title="Style"
      action={
        custom && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              edit((t) => {
                const styles = { ...t.styles };
                delete styles[k];
                return { ...t, styles };
              })
            }
          >
            <RotateCcw /> Default{DEFAULT_STYLES[k] ? "" : " (none)"}
          </Button>
        )
      }
    >
      <div className="flex gap-1">
        {(
          [
            ["bold", Bold],
            ["italic", Italic],
            ["underline", Underline],
          ] as const
        ).map(([flag, Icon]) => (
          <Button
            key={flag}
            size="icon-sm"
            variant={style[flag] ? "default" : "ghost"}
            aria-pressed={!!style[flag]}
            aria-label={flag}
            onClick={() => toggle(flag)}
            className={cn(style[flag] && "text-accent")}
          >
            <Icon />
          </Button>
        ))}
      </div>
    </Section>
  );
}

function WeechatMapping({ id }: { id: TokenId }) {
  const theme = useEditor((s) => s.draft);
  const resolved = useEditor((s) => s.resolved);
  const def = TOKEN_MAP.get(id);
  if (!def) return null;
  const c = weechatColor(makeContext(theme, resolved), hexOf(resolved, id), def.role);
  return (
    <Section title="In WeeChat">
      <div className="text-xs">
        Written as <span className="font-mono text-fg">{c.value}</span>{" "}
        {c.approximate ? (
          <span className="text-warn">
            ≈ nearest xterm-256 color (Δ {c.distance.toFixed(1)}). Link it to a palette color used
            by the ANSI palette for an exact match.
          </span>
        ) : (
          <span className="text-ok">exact (follows the terminal palette)</span>
        )}
      </div>
    </Section>
  );
}

function TokenInspector({ id }: { id: TokenId }) {
  const def = TOKEN_MAP.get(id);
  const hex = useEditor((s) => hexOf(s.resolved, id));
  const against = useEditor((s) =>
    def?.contrast ? hexOf(s.resolved, def.contrast.against as ColorId) : undefined,
  );
  const thresholds = useEditor((s) => s.thresholds);
  const broken = useEditor((s) => s.resolved.broken.includes(id));
  const edit = useEditor((s) => s.edit);
  if (!def) return null;
  const setHex = (next: Hex) =>
    edit((t) => ({ ...t, overrides: { ...t.overrides, [id]: next } }), id);
  const group = GROUPS.find((g) => g.id === def.group);
  return (
    <>
      <div className="space-y-3 border-line border-b px-4 py-4">
        <Swatch hex={hex} size="lg" aria-label={hex} />
        <div>
          <div className="text-[11px] text-faint">
            {group?.label} · {def.section}
          </div>
          <div className="font-semibold">{def.label}</div>
          {def.hint && <div className="text-muted text-xs">{def.hint}</div>}
          <div className="mt-0.5 font-mono text-[10px] text-faint">{id}</div>
        </div>
        {broken && (
          <p className="text-danger text-xs">
            This override formed a reference cycle and was ignored.
          </p>
        )}
        <OklchPicker key={id} value={hex} onChange={setHex} />
        <p className="text-[11px] text-faint">
          Editing the color here makes it a literal override. Link it to a swatch below to keep it
          following the palette.
        </p>
      </div>
      <SourcePicker id={id} />
      {def.group === "syntax" && <StyleToggles k={def.key as SyntaxKey} />}
      {def.contrast && against && (
        <Section
          title={`Contrast on ${TOKEN_MAP.get(def.contrast.against)?.label ?? "background"}`}
        >
          <ContrastCard
            fg={hex}
            bg={against}
            min={thresholds[def.contrast.level]}
            level={def.contrast.level}
            onFix={() =>
              setHex(fixContrast(hex, against, thresholds[def.contrast?.level ?? "text"]))
            }
          />
        </Section>
      )}
      {def.group === "weechat" && <WeechatMapping id={id} />}
      <Section title="Referenced by">
        <UsedBy refText={`@${id}`} />
      </Section>
    </>
  );
}

export function Inspector() {
  const selection = useEditor((s) => s.selection);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="inspector">
      {selection?.kind === "palette" && <PaletteInspector k={selection.key} />}
      {selection?.kind === "token" && <TokenInspector id={selection.id} />}
      {!selection && (
        <p className="p-6 text-muted text-sm">
          Select a swatch or a token to edit it. Alt-click anything in the preview to jump to it.
        </p>
      )}
    </div>
  );
}
